import { store } from 'store/createStore';
import * as type from 'store/actions/types';
import { localStorageProps } from 'config/constants';
import { saveInLocalStorage } from 'helpers/localStorageHelper';
import paletteHex from 'theme/paletteHex';
import paletteDark from 'theme/paletteHexDark';

var GLOBAL_STORAGE_KEY = 'slateThemeMode';

/**
 * Returns 'light' or 'dark' based on mode and system preference.
 */
export function getEffectiveTheme(mode) {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  // system mode
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

/**
 * Applies the theme by setting data-theme attribute on <html>
 * and updating the meta theme-color tag.
 */
export function applyTheme(mode) {
  var effective = getEffectiveTheme(mode);
  if (effective === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }

  // Update meta theme-color for mobile browsers
  var metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', effective === 'dark' ? paletteDark.metaThemeColor : paletteHex.white);
  }
}

/**
 * Sets theme mode: dispatches to Redux, persists to localStorage, applies to DOM.
 * @param {string} mode - 'light', 'dark', or 'system'
 * @param {string} [userUUID] - optional user UUID for user-scoped storage
 */
export function setThemeMode(mode, userUUID) {
  store.dispatch({ type: type.SET_THEME_MODE, payload: mode });
  applyTheme(mode);

  // Save globally (for flash prevention before login)
  localStorage.setItem(GLOBAL_STORAGE_KEY, mode);

  // Save user-scoped if we have a user
  if (userUUID) {
    saveInLocalStorage(userUUID, localStorageProps.THEME_MODE, mode);
  }
}

/**
 * Sets up a listener for OS theme changes (prefers-color-scheme).
 * The callback is invoked when the OS preference changes.
 * Returns a cleanup function to remove the listener.
 */
export function setupSystemThemeListener(callback) {
  if (!window.matchMedia) {
    return function() {};
  }
  var mql = window.matchMedia('(prefers-color-scheme: dark)');
  var handler = function() {
    callback();
  };

  if (mql.addEventListener) {
    mql.addEventListener('change', handler);
  } else if (mql.addListener) {
    mql.addListener(handler);
  }

  return function() {
    if (mql.removeEventListener) {
      mql.removeEventListener('change', handler);
    } else if (mql.removeListener) {
      mql.removeListener(handler);
    }
  };
}
